import { dataLayerEventKeys, pushToDataLayer } from '@asosteam/asos-web-datalayer';
import { resetDataLayer } from '@asosteam/asos-web-gtm';

const bridgeEvents = async ({ sourcePromises, sinkPromises, logError, }) => {
    Object.values(await sourcePromises).forEach(async (source) => {
        Object.values(await sinkPromises).forEach(async (sink) => {
            source.addEventListener("message", async (message) => {
                try {
                    await sink(message.data);
                }
                catch (err) {
                    logError(err);
                }
            });
        });
        source.start();
    });
};

// REF: https://experienceleague.adobe.com/docs/analytics/implementation/vars/page-vars/events/events-overview.html
var getEvents = function getEvents() {
  var eventsString = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : "";
  return eventsString.split(",").map(event => event.split("=")[0]).filter(Boolean);
};

const createAdobeSource = async (adobeDelegate) => {
    if (!adobeDelegate) {
        return null;
    }
    const { port1: privatePort, port2: publicEventTarget } = new MessageChannel();
    adobeDelegate.registerPreTrackCallback(() => {
        const data = {};
        for (const [key, value] of Object.entries(adobeDelegate)) {
            if (typeof value === "string" || typeof value === "number") {
                data[key] = value;
            }
        }
        if (!Array.isArray(data.events)) {
            data.events = getEvents(data.events);
        }
        privatePort.postMessage(data);
    });
    return publicEventTarget;
};

const createEventBusSource = async (eventBusDelegate) => {
    if (!eventBusDelegate)
        return null;
    const { port1: privatePort, port2: publicEventTarget } = new MessageChannel();
    eventBusDelegate.addWildcardListener((eventName, data) => {
        try {
            privatePort.postMessage({ events: [eventName], data });
        }
        catch (_) { } // eslint-disable-line no-empty
    });
    return publicEventTarget;
};

function ownKeys(e, r) {
  var t = Object.keys(e);
  if (Object.getOwnPropertySymbols) {
    var o = Object.getOwnPropertySymbols(e);
    r && (o = o.filter(function (r) {
      return Object.getOwnPropertyDescriptor(e, r).enumerable;
    })), t.push.apply(t, o);
  }
  return t;
}
function _objectSpread2(e) {
  for (var r = 1; r < arguments.length; r++) {
    var t = null != arguments[r] ? arguments[r] : {};
    r % 2 ? ownKeys(Object(t), !0).forEach(function (r) {
      _defineProperty(e, r, t[r]);
    }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) {
      Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r));
    });
  }
  return e;
}
function _toPrimitive(t, r) {
  if ("object" != typeof t || !t) return t;
  var e = t[Symbol.toPrimitive];
  if (void 0 !== e) {
    var i = e.call(t, r || "default");
    if ("object" != typeof i) return i;
    throw new TypeError("@@toPrimitive must return a primitive value.");
  }
  return ("string" === r ? String : Number)(t);
}
function _toPropertyKey(t) {
  var i = _toPrimitive(t, "string");
  return "symbol" == typeof i ? i : String(i);
}
function asyncGeneratorStep(gen, resolve, reject, _next, _throw, key, arg) {
  try {
    var info = gen[key](arg);
    var value = info.value;
  } catch (error) {
    reject(error);
    return;
  }
  if (info.done) {
    resolve(value);
  } else {
    Promise.resolve(value).then(_next, _throw);
  }
}
function _asyncToGenerator(fn) {
  return function () {
    var self = this,
      args = arguments;
    return new Promise(function (resolve, reject) {
      var gen = fn.apply(self, args);
      function _next(value) {
        asyncGeneratorStep(gen, resolve, reject, _next, _throw, "next", value);
      }
      function _throw(err) {
        asyncGeneratorStep(gen, resolve, reject, _next, _throw, "throw", err);
      }
      _next(undefined);
    });
  };
}
function _defineProperty(obj, key, value) {
  key = _toPropertyKey(key);
  if (key in obj) {
    Object.defineProperty(obj, key, {
      value: value,
      enumerable: true,
      configurable: true,
      writable: true
    });
  } else {
    obj[key] = value;
  }
  return obj;
}

var pageDataMapper = data => {
  var {
    href,
    pathname: pagePath,
    pageTitle,
    hostname,
    queryString,
    browseCurrency,
    browseCountry,
    floor: channel,
    platform: deviceType,
    referrer: referringUrl,
    env,
    affiliateId: affid,
    isAuthenticated,
    customerId,
    firstTimeBuyer,
    geoCountry: geoIpCountryCode,
    socialTags: {
      firstName: hashedFirstName,
      lastName: hashedLastName,
      email: hashedEmail,
      billingTelephoneMobile: hashedPhoneNumber
    } = {}
  } = data;
  return {
    event: "pageData",
    href,
    pagePath,
    pageTitle,
    hostname,
    queryString,
    browseCurrency,
    browseCountry,
    channel,
    deviceType,
    referringUrl,
    env,
    marketingData: {
      affid
    },
    customerData: _objectSpread2(_objectSpread2(_objectSpread2(_objectSpread2({
      isAuthenticated,
      customerId,
      firstTimeBuyer,
      geoIpCountryCode
    }, hashedFirstName && {
      hashedFirstName
    }), hashedLastName && {
      hashedLastName
    }), hashedEmail && {
      hashedEmail
    }), hashedPhoneNumber && {
      hashedPhoneNumber
    })
  };
};

var CATEGORY_PAGE_TYPE = "category";
var CATEGORY_PAGE_PAGE = "Category Page";
var CATEGORY_BRAND_TYPE = "brand";
var CATEGORY_BRAND_PAGE = "Brands Page";
var SEARCH_PAGE = "Search Results";
var STATIC_CURRENCY_CODE = "GBP";

var getListType = _ref => {
  var {
    pageType,
    isBrand
  } = _ref;
  if (pageType === CATEGORY_PAGE_TYPE && isBrand) {
    return CATEGORY_BRAND_TYPE;
  }
  return pageType;
};

var getImpressionListType = _ref => {
  var {
    pageType,
    isBrand
  } = _ref;
  if (pageType === CATEGORY_PAGE_TYPE && isBrand) {
    return CATEGORY_BRAND_PAGE;
  } else if (pageType === CATEGORY_PAGE_TYPE) {
    return CATEGORY_PAGE_PAGE;
  }
  return SEARCH_PAGE;
};

var pageLoad$1 = data => {
  var {
    pageType,
    returnedProductList,
    lastPage,
    pageGridSize,
    pageNumber,
    pageSize,
    totalStyles,
    personalisationStatus,
    personalisationType,
    recommendedProductsList,
    numberOfRecommendedItems,
    totalBagItems,
    totalBagValueLocalCurrency,
    floor,
    searchTerm,
    correctedSearchTerm,
    searchType,
    searchPasses,
    failedSearchType,
    breadcrumbs: breadCrumb,
    refinements,
    currency: localCurrencyCode,
    categoryID,
    isBrand
  } = data;
  return {
    event: "view_item_list",
    listType: getListType({
      pageType,
      isBrand
    }),
    returnedProductList: returnedProductList.map(_ref => {
      var {
        id
      } = _ref;
      return id;
    }),
    lastPage,
    pageGridSize,
    pageNumber,
    pageSize,
    totalStyles,
    personalisationStatus,
    personalisationType,
    recommendedProductsList,
    numberOfRecommendedItems,
    totalBagItems,
    totalBagValueLocalCurrency,
    floor,
    searchTerm,
    correctedSearchTerm,
    searchType,
    searchPasses,
    failedSearchType,
    breadCrumb,
    refinements,
    categoryID,
    ecommerce: {
      currencyCode: STATIC_CURRENCY_CODE,
      localCurrencyCode,
      impressions: returnedProductList.map((_ref2, index) => {
        var {
          description: name,
          id: productId,
          productCode,
          priceLocalCurrency,
          brandName: brand,
          colour: variant
        } = _ref2;
        return {
          name,
          productId,
          productCode,
          priceLocalCurrency,
          brand,
          variant,
          list: getImpressionListType({
            pageType,
            isBrand
          }),
          position: index + 1
        };
      })
    }
  };
};

var saveForLater = data => {
  var {
    context,
    productIID: productId,
    priceLocalCurrency,
    brandName,
    colour,
    productCode,
    description,
    currencyCode
  } = data;
  return {
    event: "saveForLater",
    context,
    ecommerce: {
      currencyCode: STATIC_CURRENCY_CODE,
      localCurrencyCode: currencyCode,
      save: {
        products: [{
          name: description,
          priceLocalCurrency,
          brand: brandName,
          variant: colour,
          quantity: 1,
          productId,
          productCode
        }]
      }
    }
  };
};

var pageLoad = data => {
  var {
    products = [],
    bagTotalValueGBP: bagTotalValueGbp,
    bagTotalValueLocalCurrency,
    netBasketValueInGBP: netBasketValueInGbp,
    netBasketValueInNetworkCurrency,
    totalItemCount,
    spendMoreToGetPremierBannerIsShowing,
    recoverBagState,
    expiredItems,
    oneOrMoreItemsAreLowInStock,
    oneOrMoreItemsAreOutOfStock
  } = data;
  return {
    event: "view_cart",
    ecommerce: {
      viewCart: {
        products: products.map(_ref => {
          var {
            productName: name,
            productVariantId: variantId,
            productIID: productId,
            productBrand: brand,
            productQuantity: quantity,
            productPriceGBP: priceGbp,
            productPriceLocalCurrency: priceLocalCurrency,
            markedDownPrice
          } = _ref;
          return {
            name,
            variantId,
            productId,
            brand,
            quantity,
            priceGbp,
            priceLocalCurrency,
            markedDownPrice
          };
        })
      }
    },
    bagTotalValueGbp,
    bagTotalValueLocalCurrency,
    netBasketValueInGbp,
    netBasketValueInNetworkCurrency,
    totalItemCount,
    spendMoreToGetPremierBannerIsShowing,
    recoverBagState,
    expiredItems,
    oneOrMoreItemsAreLowInStock,
    oneOrMoreItemsAreOutOfStock
  };
};

var addPremierToBag = data => {
  var {
    addItemType,
    premierUpsellDisplayed,
    price,
    subscriptionType
  } = data;
  return {
    event: "add_to_cart",
    addItemType,
    subscriptionType,
    premierUpsellDisplayed,
    ecommerce: {
      add: {
        products: [{
          name: "subscription",
          id: 1,
          price,
          brand: "premier",
          category: "premier",
          quantity: 1
        }]
      }
    }
  };
};

var consentChanged = consentGroups => ({
  event: "OneTrustGroupsUpdated",
  OnetrustActiveGroups: consentGroups
});

var eventMap = {
  "tracking.pageData.ready": pageDataMapper,
  "tracking.productPage.pageLoad": data => data,
  "tracking.productPage.addToBag": data => data,
  "tracking.productPage.saveForLater": data => data,
  "tracking.productList.pageLoad": pageLoad$1,
  "tracking.productList.saveForLater": saveForLater,
  "tracking.asosBag.pageLoad": pageLoad,
  "tracking.asosBag.addItem": addPremierToBag,
  "tracking.savedLists": data => data,
  "onetrust.consentChanged": consentChanged
};

var handleEventImportReady;
var handleEventStub = new Error("\"@asosteam/asos-web-gtm\" dependency is missing");
var initialiseAsosWebGtm = /*#__PURE__*/function () {
  var _ref = _asyncToGenerator(function* (gtmId, nonce) {
    handleEventImportReady = new Promise((resolve, reject) => {
      import('@asosteam/asos-web-gtm').then(asosWebGtm => {
        var {
          handleEvent
        } = asosWebGtm.initialise(gtmId, nonce);
        resolve(handleEvent);
      }).catch(() => {
        reject(handleEventStub);
      });
    });
  });
  return function initialiseAsosWebGtm(_x, _x2) {
    return _ref.apply(this, arguments);
  };
}();
var handleEvent$1 = /*#__PURE__*/function () {
  var _ref2 = _asyncToGenerator(function* (data) {
    if (!handleEventImportReady) {
      throw new Error("initialiseAsosWebGtm must be called before handleEvent");
    }
    var handleEventFunction = yield handleEventImportReady;
    yield handleEventFunction(data);
  });
  return function handleEvent(_x3) {
    return _ref2.apply(this, arguments);
  };
}();

var getChannelImport;
var getChannelStub = () => {
  throw new Error("\"@asosteam/asos-web-secure-script-container\" dependency is missing");
};
var getChannel = /*#__PURE__*/function () {
  var _ref = _asyncToGenerator(function* () {
    if (!getChannelImport) {
      yield import('@asosteam/asos-web-secure-script-container').then(asosWebSecureScriptContainer => {
        getChannelImport = asosWebSecureScriptContainer.getChannel;
      }, () => {
        getChannelImport = getChannelStub;
      });
    }
    return getChannelImport();
  });
  return function getChannel() {
    return _ref.apply(this, arguments);
  };
}();

var pushEvent = /*#__PURE__*/function () {
  var _ref2 = _asyncToGenerator(function* (_ref) {
    var {
      id,
      eventName,
      data,
      logError,
      includeSecureScriptContainer = true
    } = _ref;
    if (!(data !== null && data !== void 0 && data.event)) {
      return null;
    }
    var port;
    if (includeSecureScriptContainer) {
      ({
        port
      } = yield getChannel());
    }
    try {
      yield handleEvent$1(data);
      if (includeSecureScriptContainer) {
        port.postMessage({
          eventName,
          data
        });
      }
    } catch (err) {
      logError(err, {
        id,
        eventName,
        data
      });
    }
  });
  return function pushEvent(_x) {
    return _ref2.apply(this, arguments);
  };
}();

var isValidObject = eventData => !!eventData && !Array.isArray(eventData) && typeof eventData === "object";
var isValidString = eventData => typeof eventData === "string";
var isValidDataForGTM = (eventName, eventData) => {
  if (eventName === "onetrust.consentChanged") {
    return isValidString(eventData);
  }
  return isValidObject(eventData);
};

const handleEvent = async ({ eventName, payload, id, logError, includeSecureScriptContainer, }) => {
    const eventMapper = eventMap[eventName];
    if (!isValidDataForGTM(eventName, payload.data)) {
        return;
    }
    if (eventMapper) {
        await pushEvent({
            id,
            eventName,
            data: eventMapper(payload.data),
            logError,
            includeSecureScriptContainer,
        });
    }
    else if (eventName.startsWith("tracking.gtm.") ||
        dataLayerEventKeys.includes(eventName)) {
        await pushEvent({
            id,
            eventName,
            data: payload.data,
            logError,
            includeSecureScriptContainer,
        });
    }
};
const createGtmSink = (gtmId, logError, includeSecureScriptContainer = true, nonce = undefined) => {
    if (!gtmId || !logError) {
        return null;
    }
    initialiseAsosWebGtm(gtmId, nonce);
    const gtm = async (payload) => {
        if (!payload.events) {
            return null;
        }
        for (const eventName of payload.events) {
            await handleEvent({
                eventName,
                payload,
                id: gtmId,
                logError,
                includeSecureScriptContainer,
            });
        }
    };
    return gtm;
};

const event1={eventKey:"View Product Page",eventTags:{eVar60:{key:"eVar60"}}};const event3={eventKey:"Successful Search",eventTags:{searchTerm:{key:"eVar25"}}};const event4={eventKey:"SearchResultNoProducts",eventTags:{}};const event5={eventKey:"RefinementsUsed",eventTags:{refinementType:{key:"prop13"}}};const event43={eventKey:"Product View",eventTags:{stockStatus:"In Stock"}};const event10={eventKey:"Add To Saved",eventTags:{productID:{key:"products"},eVar60:{key:"eVar60"}}};const event44={eventKey:"Product View",eventTags:{stockStatus:"Out Of Stock"}};const event45={eventKey:"Product View",eventTags:{stockStatus:"Partially Out Of Stock"}};const purchase={eventKey:"OrderComplete-BR",eventTags:{orderID:{key:"purchaseID"},revenue:{key:"event36"},value:{key:"quantity"}}};var manifest = {event1:event1,event3:event3,event4:event4,event5:event5,event43:event43,event10:event10,event44:event44,event45:event45,"tracking.productList.pageLoad":{eventKey:"View PLP Page",eventTags:{}},purchase:purchase};

var ADOBE_VISITS_PAGE_LOAD_COUNT_KEY = "eVar225";
var notBouncedVisitAlreadySent = false;
var captureVisitMetrics = _ref => {
  var {
    features,
    data
  } = _ref;
  var visitPageCount = parseInt(data[ADOBE_VISITS_PAGE_LOAD_COUNT_KEY]);
  if (isNaN(visitPageCount)) {
    return;
  }
  var coreAttributes = features.getCoreAttributes();
  coreAttributes.visitPageCount = visitPageCount;
  if (visitPageCount < 2) {
    delete coreAttributes.notBouncedVisit;
  } else {
    coreAttributes.notBouncedVisit = true;
  }
  features.setCoreAttributes(coreAttributes);
  if (visitPageCount === 2 && !notBouncedVisitAlreadySent) {
    notBouncedVisitAlreadySent = true;
    features.track("Not Bounced Visit");
  }
};

var mapTagValues = (value, data, tag) => {
  if (typeof value === "object") {
    if (tag === "revenue") {
      return parseInt(data[value.key] * 100);
    }
    return data[value.key];
  }
  return value;
};
var parseEventTags = (eventTags, data) => {
  var parsedEventTags = {};
  var mappedEntries = Object.entries(eventTags).map(_ref => {
    var [tag, value] = _ref;
    return [tag, mapTagValues(value, data, tag)];
  });
  for (var [key, value] of mappedEntries) {
    parsedEventTags[key] = value;
  }
  return parsedEventTags;
};

const createOptimizelySink = (featuresDelegate) => async (data) => {
    if (!featuresDelegate || !data.events) {
        return null;
    }
    let features;
    try {
        features = await featuresDelegate;
    }
    catch (_) {
        return;
    }
    const { success } = await features.onReady();
    if (!success) {
        return;
    }
    captureVisitMetrics({ features, data });
    data.events.forEach((eventName) => {
        const event = manifest[eventName];
        if (!event) {
            return;
        }
        const { eventKey, eventTags } = event;
        const parsedEventTags = parseEventTags(eventTags, data);
        features.track(eventKey, null, null, parsedEventTags);
    });
};

const bridgeDirectSources = ({ sinkPromises, logError, }) => {
    return {
        pushToDataLayer: async (definitions) => {
            const { gtm } = await sinkPromises;
            return pushToDataLayer({
                dataLayerSink: gtm,
                logError,
            }, definitions);
        },
        resetDataLayer: async () => {
            await sinkPromises;
            return resetDataLayer();
        },
    };
};

const chainEntriesPromises = async (entries) => {
    const resolved = await Promise.all(Object.entries(entries).map(async ([key, value]) => [key, await value]));
    return Object.fromEntries(resolved);
};

/**
 * This callback resolves to an Adobe Analytics SDK instance.
 * @callback adobeDelegate
 * @returns {external:AdobeAnalytics.SDK|Promise<external:AdobeAnalytics.SDK>}
 */
/**
 * This callback resolves to a Features SDK instance.
 * @callback featuresDelegate
 * @returns {external:ASOSWebFeatures.FeaturesInstance|Promise<external:ASOSWebFeatures.FeaturesInstance>}
 */
/**
 * This callback resolves to an Event Bus instance.
 * @callback eventBusDelegate
 * @returns {external:ASOSWeb.EventBus|Promise<external:ASOSWeb.EventBus>}
 */
/**
 * This callback takes an error and handles logging it on the consumer side
 * @callback logError
 * @returns {void}
 */
/**
 * Initialise the Event Tracking Bridge.
 *
 * All parameters should be delegates that resolve to the required instance.
 * The Event Tracking Bridge coordinates the awaiting on each delegate
 * internally to ensure that sources, sinks, and logging do not block each
 * other from initialising as soon as possible.
 *
 * This allows the consumer to call this method as soon as possible in
 * their code in order to initialise sources and sinks before any events
 * are missed.
 *
 * @function
 * @param {Object} params
 * @param {adobeDelegate} params.adobe Delegate for an Adobe Analytics SDK instance.
 * @param {eventBusDelegate} [params.eventBus] Delegate for an Event Bus instance.
 * @param {featuresDelegate} params.features Delegate for a Features SDK instance.
 * @param {string} [params.gtmId] The ID for the GTM instance.
 * @param {logError} [params.logError] callback takes an error and handles logging it on the consumer side
 * @returns {Promise<void>}
 * @public
 * @example
 *
 * import {
 *   initialise,
 *   createAdobeSource,
 *   createEventBusSource,
 *   createGtmSink,
 *   createOptimizelySink
 * } from "asos-web-event-tracking-bridge";
 *
 * initialise({
 *   sources: {
 *     adobe: createAdobeSource(),
 *     eventBus: createEventBusSource()
 *   },
 *   sinks: {
 *     gtm: createGtmSink(),
 *     optimizely: createOptimizelySink()
 *   },
 *   logError: window.newrelic.noticeError
 * });
 */
const initialise = ({ sources = {}, sinks = {}, logError, }) => {
    const sinkPromises = chainEntriesPromises(sinks);
    const sourcePromises = chainEntriesPromises(sources);
    bridgeEvents({
        sourcePromises,
        sinkPromises,
        logError,
    });
    return bridgeDirectSources({ sinkPromises, logError });
};

export { createAdobeSource, createEventBusSource, createGtmSink, createOptimizelySink, initialise };
//# sourceMappingURL=browser.js.map
